import { create } from "zustand";
import { devtools } from "zustand/middleware";
import axios from "axios";
import { produce } from "immer";
import { findIndex } from "lodash";
import fileDownload from "js-file-download";

const initialState = {
  gettingBalanceSheet: false,
  balanceSheetError: {},
  balanceSheet: [],
  downloadingBalanceSheet: false,
  downloadBalanceSheetError: {},
  // project-level
  gettingProjectBalanceSheet: false,
  projectBalanceSheetError: {},
  projectBalanceSheet: [],
  downloadingProjectBalanceSheet: false,
  downloadProjectBalanceSheetError: {},
};

const store = (set, get) => ({
  ...initialState,

  async getBalanceSheet(context) {
    set({ gettingBalanceSheet: true, balanceSheetError: {} });
    try {
      // If projectId is provided, use project endpoint
      const endpoint = context.projectId
        ? `/finance/balance-sheet/projects/${context.projectId}`.replace(/\/$/, "")
        : "/finance/balance-sheet".replace(/\/$/, "");
      
      const params = {};
      if (context.startDate && context.endDate) {
        params.startDate = context.startDate;
        params.endDate = context.endDate;
      } else if (context.asOfDate) {
        params.asOfDate = context.asOfDate;
      }
      
      const res = await axios.get(endpoint, { params });

      // Handle response structure - http.serverSuccess wraps the response
      // Backend returns: { success: true, message: "...", data: { assets: {...}, liabilities: {...}, equity: {...}, totals: {...} } }
      // Axios interceptor returns response.data, so res is the wrapped object
      const responseData = res?.data || res;

      // Use project-specific store if projectId provided
      const records = context.projectId ? get().projectBalanceSheet : get().balanceSheet;
      const index = findIndex(
        records,
        (record) => {
          if (context.projectId) {
            // Project balance sheet: match by projectId and date params
            if (context.startDate && context.endDate) {
              return (
                record.startDate === context.startDate &&
                record.endDate === context.endDate &&
                record.projectId === context.projectId
              );
            } else {
              return (
                record.asOfDate === context.asOfDate &&
                record.projectId === context.projectId
              );
            }
          } else {
            // General balance sheet: match by date params
            if (context.startDate && context.endDate) {
              return (
                record.startDate === context.startDate &&
                record.endDate === context.endDate
              );
            } else {
              return record.asOfDate === context.asOfDate;
            }
          }
        }
      );

      if (context.projectId) {
        // Store in projectBalanceSheet
        if (index > -1) {
          set(
            produce((state) => {
              state.projectBalanceSheet[index] = {
                data: responseData,
                ...context,
              };
            })
          );
        } else {
          set(
            produce((state) => {
              state.projectBalanceSheet.push({
                data: responseData,
                ...context,
              });
            })
          );
        }
      } else {
        // Store in general balanceSheet
        if (index > -1) {
          set(
            produce((state) => {
              state.balanceSheet[index] = {
                data: responseData,
                ...context,
              };
            })
          );
        } else {
          set(
            produce((state) => {
              state.balanceSheet.push({
                data: responseData,
                ...context,
              });
            })
          );
        }
      }
    } catch (err) {
      console.error("Balance Sheet API Error:", err);
      console.error("Error Details:", {
        status: err?.response?.status,
        statusText: err?.response?.statusText,
        data: err?.response?.data,
        message: err?.message,
      });
      console.error("Request URL:", `/finance/balance-sheet?asOfDate=${context.asOfDate}`);
      set({ balanceSheetError: err });
    } finally {
      set({ gettingBalanceSheet: false });
    }
  },

  async downloadBalanceSheet(context, fileName = "BALANCE_SHEET.xlsx") {
    set({ downloadingBalanceSheet: true, downloadBalanceSheetError: {} });
    
    const params = {};
    if (context.projectId) {
      params.projectId = context.projectId;
    }
    if (context.startDate && context.endDate) {
      params.startDate = context.startDate;
      params.endDate = context.endDate;
    } else if (context.asOfDate) {
      params.asOfDate = context.asOfDate;
    }
    
    await axios
      .get("/finance/balance-sheet/download-excel", {
        params,
        responseType: "blob",
      })
      .then((res) => {
        fileDownload(res.data, fileName);
      })
      .catch((err) => {
        set({ downloadBalanceSheetError: err });
      })
      .finally(() => set({ downloadingBalanceSheet: false }));
  },
});

const storeName = "Balance Sheet Store";

const useBalanceSheetStore = create(devtools(store, { storeName }), { storeName });

export default useBalanceSheetStore;



